import { useTheme } from '@mui/material/styles'
import { Box, Divider, Drawer, Stack, Typography } from '@mui/material'
import { useCart, useOrdering, useCurrentStore } from 'hooks'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import { generatePath, useNavigate } from 'react-router-dom'
import { TypeRoute } from 'core/TypeRoute'
import { KButton, KIconButton } from 'components/core/KButton'
import { OrderItemsList } from './OrderItemsList'
import { OrderSummary } from './OrderSummary'
import { useState } from 'react'
import { useCurrentUser } from 'hooks/AuthHooks'
import { LoginSuggestionDialog } from 'components/account/LoginSuggestionDialog'
import { useSelector } from 'react-redux'
import { RootState } from 'store'

export const CartDrawer = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { hideCart, isCartVisible } = useCart()
  const { order, clearSubmitOrderState, canSubmitOrder } = useOrdering()
  const { store } = useCurrentStore()
  const { isSignedIn } = useCurrentUser()
  const [showLoginDialog, setShowLoginDialog] = useState(false)
  const isContinueAsGuest = useSelector(
    (state: RootState) => state.ordering.isContinueAsGuest,
  )

  const tableInfo = store?.table_info

  const navigate = useNavigate()

  const proceedToCheckout = () => {
    hideCart()
    clearSubmitOrderState()
    if (tableInfo) {
      return navigate(
        generatePath(TypeRoute.TableSubmitOrder, {
          storeId: store?.id ?? '',
          tableId: tableInfo.table_id,
        }),
      )
    }

    navigate(
      generatePath(TypeRoute.SubmitOrder, {
        storeId: store?.id ?? '',
      }),
    )
  }

  const handleSubmitOrder = () => {
    if (store.is_show_auth && !isSignedIn && !isContinueAsGuest) {
      setShowLoginDialog(true)
    } else {
      proceedToCheckout()
    }
  }

  const handleCloseLoginDialog = () => {
    setShowLoginDialog(false)
  }

  if (!store || !order) {
    return null
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={isCartVisible}
        onClose={hideCart}
        sx={{
          '& .MuiDrawer-paper': {
            width: 560,
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          },
        }}
      >
        <Stack
          sx={{
            height: '100%',
            paddingX: 2,
            [theme.breakpoints.down('sm')]: {
              paddingX: 1,
            },
          }}
          role="presentation"
        >
          <Stack
            sx={{
              height: theme.mixins.toolbar.minHeight,
              minHeight: theme.mixins.toolbar.minHeight,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="h4"
              color="primary"
              sx={{ flexGrow: 1 }}
              textAlign="center"
            >
              {t('Cart')}
            </Typography>
            <KIconButton onClick={hideCart}>
              <CloseIcon />
            </KIconButton>
          </Stack>
          <Divider />
          <Box sx={{ flexGrow: 1, paddingY: 2, overflowY: 'auto' }}>
            {order.subtotal === 0 ? (
              <Typography
                textAlign="center"
                variant="body2"
                margin={4}
                color="secondary"
              >
                {t('Your shopping cart is currently empty.')}
              </Typography>
            ) : (
              <OrderItemsList />
            )}
          </Box>
          <Divider />
          <OrderSummary sx={{ marginY: 1.5 }} showTip={false} />
          <Divider />
          <Stack sx={{ paddingY: 1 }}>
            <KButton disabled={!canSubmitOrder} onClick={handleSubmitOrder}>
              {t('Checkout')}
            </KButton>
          </Stack>
        </Stack>
      </Drawer>

      <LoginSuggestionDialog
        open={showLoginDialog}
        onClose={handleCloseLoginDialog}
        onContinueAsGuest={proceedToCheckout}
      />
    </>
  )
}
